import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";
import Viewer from "./Viewer";
import Toolbar from "./Toolbar";
import ExtractedDataToolbar from "../Toolbars/ExtractedDataToolbar";
import DetectTypesToolbar from "../Toolbars/DetectTypesToolbar";
import Pagination from "./Pagination";
import Loading from "../Loading";
import SigantureDocToolbar from "../Toolbars/SigantureDocToolbar";
import SigantureEnvelopeToolbar from "../Toolbars/SignatureEnvelopeToolbar";
import { useRequestHook } from "../../utils/hooks/hooks";
import { Modal, ModalBody, ModalHeader, ModalFooter, Col } from "shards-react";
import AdditionalData from "./AdditionalData";
import SaveAndSubmit from "../Toolbars/components/SaveAndSubmit";
import ReactivateDocument from "../Toolbars/components/ReactivateDocument";
import {
  addPagetoActualDocument,
  updateReference,
  updateRecognisedRef,
  addDocumentInRedux,
} from "../../utils/redux/actions";
import ReferenceNew from "components/Reference/ReferenceNew";
import Reference from "components/Reference/Reference";

/**
 * Export a component that contain all the data to display
 * page thumbnail, inputs with recognized words and etc.
 */

const DocumentsModal = ({
  dispatch,
  documentId,
  isModalOpen,
  toggleModal,
  mainModule,
  serviceType,
  envelopeSelectedDoc,
  envelopeName,
  additionalTabs,
  thumbnails,
  displayReference,
  updateDocumentsList,
  isApproved,
  pages,
  pagesLength,
  additionalFields,
  signatureFields,
  withCategory,
  diva,
  modulesOnFinish,
}) => {
  const { handleRequest } = useRequestHook();

  const [pageToDisplay, setPageToDisplay] = useState({ actual: 0 }); //Define a default page number.
  const [fieldToBeUpdated, setFieldToBeUpdated] = useState(false); //Define a default variable for when fields should be rendered.
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(envelopeSelectedDoc);
  const isMountedRef = useRef(null);
  const [alertMessage, setAlertMessage] = useState();
  // For Signature envelopes, the first document should be loaded by default. Set service_type to null for further requests
  const default_doc =
    envelopeSelectedDoc && envelopeSelectedDoc !== documentId ? false : true;

  const reactivationHandler = useCallback(
    (confirmReactivate) => {
      handleRequest(
        "post",
        "/reactivate/reactivate-document",
        { documentId },
        (response) => {
          if (response) {
            updateDocumentsList((oldDocList) =>
              oldDocList.filter(
                (document) => document.documentId !== documentId
              )
            );
            toggleModal();
            return dispatch(addDocumentInRedux()); //empty the docment in redux
          } else {
            setAlertMessage({
              msg: "Das Dokument kann nicht reaktivieren werden!",
            });

            confirmReactivate();
          }
        }
      );
    },
    [documentId, handleRequest, toggleModal, updateDocumentsList]
  );

  const additionalFieldsData = useCallback(() => {
    if (!additionalTabs || !additionalFields) return false;

    return additionalTabs.reduce(
      (obj, tabName) => ({ ...obj, [tabName]: additionalFields[tabName] }),
      {}
    );
  }, [additionalFields, additionalTabs]);

  const saveHandler = useCallback(
    ({ newOrder, handleRemovedPage }) => {
      // Save doc in DB
      const { reference, recognisedRef } =
        document.getElementById("detect-types-form")?.elements;
      if (!reference)
        return setAlertMessage({ msg: "Die Referenznummer fehlt!" });
      const { value: refValue } = reference;
      const recRefVal = recognisedRef?.value ?? "";

      handleRequest(
        "post",
        "/thumbnails/update-thumbnails",
        {
          thumbnails: newOrder ? newOrder : thumbnails,
          pagesLength,
          documentId,
          approved: false,
          reference: refValue,
          recognisedRef: recRefVal,
          additionalFields: additionalFieldsData(),
        },
        (response, msg) => {
          if (isMountedRef.current !== true && !response?.info?.length) return;

          if (!response?.info?.length) {
            setAlertMessage({
              msg: msg ?? "Das Dokument kann nicht gespeichert werden!",
            });

            if (newOrder) return handleRemovedPage();

            setTimeout(() => {
              window.location.reload();
              return;
            }, 2000);
          } else {
            const updateInfoElems = (oldElements) => {
              const names = oldElements.map(({ name }) => name);
              response?.info?.forEach(
                ({ name, value }) =>
                  (oldElements[names.indexOf(name)].value = value)
              );
              return oldElements;
            };

            updateDocumentsList((oldDocList) =>
              oldDocList.map((docInfo) =>
                docInfo.documentId === documentId
                  ? {
                      ...docInfo,
                      infoBox: updateInfoElems([...docInfo.infoBox]),
                    }
                  : docInfo
              )
            );
            dispatch(updateReference(response?.references));
            dispatch(updateRecognisedRef(response?.recognisedRefs));

            if (isMountedRef.current !== true) return;

            if (newOrder) return handleRemovedPage(newOrder);

            setAlertMessage({
              msg: "Das Dokument wurde gespeichert!",
              background: "success",
            });
          }
        }
      );
    },
    [
      additionalFieldsData,
      dispatch,
      documentId,
      handleRequest,
      pagesLength,
      updateDocumentsList,
      thumbnails,
    ]
  );

  const isAdditionalDataNotExist = useCallback(() => {
    if (!additionalTabs) return false;
    if (!additionalFields) return true;

    let result;

    additionalTabs.forEach((tab) => {
      const tabData = mainModule["-additionalFields"][tab];
      const filteredValues = Object.values(tabData).filter(
        ({ displayName, type, options }) => {
          if (type === "text") return !additionalFields[tab][displayName];

          return !options.includes(additionalFields[tab][displayName]);
        }
      );

      if (filteredValues.length > 0) return (result = true);
    });

    return result;
  }, [additionalFields, additionalTabs, mainModule]);

  const submitHandler = useCallback(() => {
    const notDetectedPages = thumbnails?.columns?.not_detected?.pageIds?.length;
    const { reference, recognisedRef } =
      document.getElementById("detect-types-form")?.elements;

    if (!reference)
      return setAlertMessage({ msg: "Die Referenznummer fehlt!" });
    if (reference.classList.contains("is-invalid"))
      return setAlertMessage({ msg: "Diese Referenz ist nicht gültig!" });
    const { value: refValue } = reference;
    if (refValue === "")
      return setAlertMessage({
        msg: "Bitte tragen Sie eine Referenznummer ein!",
      });

    const recRefVal = recognisedRef?.value ?? "";
    if (recRefVal)
      return setAlertMessage({
        msg: "Bitte wählen Sie eine Kategorie von der Referenznummer aus!",
      });

    if (notDetectedPages)
      return setAlertMessage({
        msg: "Das Dokument enthält nicht zugeordnete Seiten!",
      });

    if (isAdditionalDataNotExist())
      return document.getElementById("additional-data-button").click();

    return document.getElementById("detect-types-form").requestSubmit();
  }, [isAdditionalDataNotExist, thumbnails]);

  const headerContent = useMemo(
    () => (
      <>
        <Col tag={Col} xs={12} sm={5} md={6} className="p-0">
          {displayReference &&
            (withCategory ? <ReferenceNew /> : <Reference />)}
        </Col>
        <Col xs={2} sm={1} md={2} className="text-center p-0">
          {additionalTabs && (
            <AdditionalData mainModuleData={mainModule["-additionalFields"]} />
          )}
        </Col>
        {isApproved &&
          serviceType !== "signature_docs" &&
          serviceType !== "signature_envelopes" && (
            <ReactivateDocument reactivationHandler={reactivationHandler} />
          )}
        {!isApproved && serviceType === "detect_types" && (
          <SaveAndSubmit
            saveHandler={saveHandler}
            submitHandler={submitHandler}
            submitText={"Freigeben"}
          />
        )}
      </>
    ),
    [
      additionalTabs,
      displayReference,
      isApproved,
      mainModule,
      reactivationHandler,
      saveHandler,
      submitHandler,
      serviceType,
      withCategory,
    ]
  );

  const bodyContent = useMemo(
    () => (
      <>
        <Viewer
          pageToDisplay={pageToDisplay}
          updateDocumentsList={updateDocumentsList}
          withFormFields={
            serviceType === "extract_data" || serviceType === "signature_docs"
          }
          fieldToBeUpdated={fieldToBeUpdated}
        />
        <Toolbar>
          {serviceType === "extract_data" ? (
            <ExtractedDataToolbar
              setFieldToBeUpdated={setFieldToBeUpdated}
              fieldsNames={mainModule["-fields"].map(({ name }) => name)}
              fieldDisplayNames={mainModule["-fields"].map(
                ({ displayName }) => displayName
              )}
              fieldTypes={mainModule["-fields"].map(({ type }) => type)}
            />
          ) : serviceType === "signature_docs" ? (
            <SigantureDocToolbar
              setFieldToBeUpdated={setFieldToBeUpdated}
              fieldsNames={signatureFields.map(({ name }) => name)}
              fieldDisplayNames={signatureFields.map(
                ({ displayName }) => displayName
              )}
              fieldTypes={signatureFields.map(({ type }) => type)}
              reactivationHandler={reactivationHandler}
            />
          ) : serviceType === "signature_envelopes" ? (
            <SigantureEnvelopeToolbar
              setFieldToBeUpdated={setFieldToBeUpdated}
              envelopeName={envelopeName}
              fieldsNames={signatureFields.map(({ name }) => name)}
              fieldDisplayNames={signatureFields.map(
                ({ displayName }) => displayName
              )}
              fieldTypes={signatureFields.map(({ type }) => type)}
              reactivationHandler={reactivationHandler}
              modulesOnFinish={modulesOnFinish}
            />
          ) : serviceType === "detect_types" ? (
            <DetectTypesToolbar
              setPageToDisplay={setPageToDisplay}
              updateDocumentsList={updateDocumentsList}
              mainModuleAdditionalFields={mainModule["-additionalFields"]}
              showComment={mainModule["-commentField"]}
              sortByPage={mainModule["-sortByPage"]}
              closeModal={toggleModal}
              alertMessage={alertMessage}
              setAlertMessage={setAlertMessage}
              saveHandler={saveHandler}
            />
          ) : undefined}
        </Toolbar>
      </>
    ),
    [
      envelopeName,
      fieldToBeUpdated,
      mainModule,
      pageToDisplay,
      serviceType,
      toggleModal,
      updateDocumentsList,
      alertMessage,
      saveHandler,
      reactivationHandler,
    ]
  );

  useEffect(() => {
    const storedWidth = localStorage.getItem("customWidth");
    const cssProperty =
      document.documentElement.style.getPropertyValue("--custom-width");

    isMountedRef.current = true;

    if (storedWidth && storedWidth !== cssProperty)
      document.documentElement.style.setProperty("--custom-width", storedWidth);

    return () => {
      if (
        document.documentElement.style.getPropertyValue("--custom-width") &&
        document.documentElement.style.getPropertyValue("--custom-width") !==
          localStorage.getItem("customWidth")
      ) {
        localStorage.setItem(
          "customWidth",
          document.documentElement.style.getPropertyValue("--custom-width")
        ); // store value in local storage
      }
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const { actual, preview } = pageToDisplay;
    const displayedPage = preview ?? actual;

    if (displayedPage === 0) return;

    const pagesToRequest = ((page) => {
      const result = [];

      const isPageNeedToBeRequsted = (pageToCheck) => {
        if (
          !(pageToCheck in pages) &&
          !result.includes(pageToCheck) &&
          pageToCheck > 0 &&
          pageToCheck < pagesLength
        )
          return true;

        return false;
      };

      if (isPageNeedToBeRequsted(page)) {
        result.push(page);
      }

      for (let i = 1; result.length < 3; i++) {
        const prevPage = page - i;
        const nextPage = page + i;

        if (
          !isPageNeedToBeRequsted(prevPage) &&
          !isPageNeedToBeRequsted(nextPage)
        ) {
          break;
        }

        if (isPageNeedToBeRequsted(prevPage)) {
          result.unshift(prevPage);
        }

        if (isPageNeedToBeRequsted(nextPage)) {
          result.push(nextPage);
        }
      }
      return result;
    })(displayedPage);

    if (!pagesToRequest.length) return;

    handleRequest(
      "post",
      "/documents/load-selected-document",
      {
        serviceType: default_doc ? serviceType : null,
        documentId: default_doc ? documentId : envelopeSelectedDoc,
        pagesToRequest,
      },
      (response) => {
        if (response) dispatch(addPagetoActualDocument(response));
      }
    ); // Request the selected document from DB.
  }, [
    pageToDisplay,
    default_doc,
    dispatch,
    documentId,
    envelopeSelectedDoc,
    pagesLength,
    serviceType,
    pages,
    handleRequest,
  ]);

  useEffect(() => {
    if (documentId && loading) setLoading((oldState) => !oldState);

    // If document has changed set page to display = 0
    if (lastDoc && lastDoc !== envelopeSelectedDoc && !diva) {
      setPageToDisplay({ actual: 0 });
    }

    // Update last_doc
    setLastDoc(envelopeSelectedDoc);
  }, [documentId, envelopeSelectedDoc, lastDoc, loading, diva]);

  useEffect(() => {
    if ((!pages || Object.keys(pages)?.length === 0) && diva) {
      setPageToDisplay({ actual: "diva-original-1" });
    }
  }, [pages, diva]);

  return (
    <Modal
      centered
      size="lg"
      className="mt-2"
      open={isModalOpen}
      toggle={() => null}
    >
      {loading ? (
        <Loading style={{ height: "calc(80vh + 58px)" }} />
      ) : (
        <>
          <ModalHeader
            className="px-3 py-2 d-flex min-height-55"
            toggle={toggleModal}
            titleClass="d-flex w-100 flex-wrap"
          >
            {headerContent}
          </ModalHeader>
          <ModalBody className="row p-0 mx-0 justify-content-center vh-80">
            {bodyContent}
          </ModalBody>
          <ModalFooter className="py-2 px-0">
            <Pagination
              currentPage={pageToDisplay.actual}
              setPageToDisplay={setPageToDisplay}
            />
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

/**
 * Take the id of saved in redux document and return it
 * in components props.
 */
const mapStateToProps = ({
  savedDocument: {
    id: documentId,
    envelopeSelectedDoc,
    envelopeName,
    additionalTabs,
    thumbnails,
    approved: isApproved,
    pages,
    pagesLength,
    additionalFields,
    diva,
  },
  userData: { services },
}) => {
  const _services = services.filter((x) => x.signatureFields !== null);
  const signatureFields = _services[0]?.signatureFields ?? [];
  const [currentService] = services.filter(
    (x) => x.id === window.location.pathname.split("/")[2]
  );
  const withCategory = currentService.mainModule?.["-withCategory"];

  return {
    documentId,
    envelopeSelectedDoc,
    envelopeName,
    additionalTabs,
    thumbnails,
    isApproved,
    pages,
    pagesLength,
    additionalFields,
    signatureFields,
    withCategory: withCategory ?? false,
    diva,
  };
};

export default connect(mapStateToProps)(DocumentsModal); //Export component and connect it with redux.
